import { useState, useEffect, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';
import { type FilterValue } from '../../SbSharedRefineMenu/SbSharedRefineMenuMultiSelect/SbSharedRefineMenuMultiSelect.tsx';

import { SbRespRefineMenuStoneCutSelect } from '../SbRespRefineMenuStoneCutSelect/SbRespRefineMenuStoneCutSelect.tsx';
import { SbSharedRefineMenuTypeaheadContainer } from '../../SbSharedRefineMenu/SbSharedRefineMenuTypeahead/SbSharedRefineMenuTypeaheadContainer.tsx';

import { type SbRespRefineMenuFilterStoneCut_viewer$key } from './__generated__/SbRespRefineMenuFilterStoneCut_viewer.graphql.ts';
import { type SbRespRefineMenuFilterStoneCut_itemSearch$key } from './__generated__/SbRespRefineMenuFilterStoneCut_itemSearch.graphql.ts';

const viewerFragment = graphql`
    fragment SbRespRefineMenuFilterStoneCut_viewer on Viewer {
        ...SbSharedRefineMenuTypeaheadContainer_viewer
    }
`;
const itemSearchFragment = graphql`
    fragment SbRespRefineMenuFilterStoneCut_itemSearch on ItemSearchQueryConnection {
        ...SbRespRefineMenuStoneCutSelect_itemSearch
        ...SbSharedRefineMenuTypeaheadContainer_itemSearch
    }
`;

type Props = {
    viewer: SbRespRefineMenuFilterStoneCut_viewer$key;
    itemSearch: SbRespRefineMenuFilterStoneCut_itemSearch$key;
    filterName: string;
    trackingOptions?: Record<string, unknown>;
    values: FilterValue[];
};

export const SbRespRefineMenuFilterStoneCut: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    filterName,
    trackingOptions,
    values,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const afterDisplay = useSbSelector(state => state.relayVariables.variables.afterDisplay);

    const [stoneCutMap, setStoneCutMap] = useState<Record<string, FC<{ className?: string }>>>({});

    useEffect(() => {
        if (afterDisplay) {
            import(
                /* webpackChunkName: "sbRespRefineMenuStoneCutComponentMap" */
                './sbRespRefineMenuStoneCutComponentMap.ts'
            ).then(moduleExport => {
                setStoneCutMap(moduleExport.stoneCutMap);
            });
        }
    }, [afterDisplay]);

    return (
        <SbSharedRefineMenuTypeaheadContainer
            filterName={filterName}
            availableFilterValues={values}
            viewer={viewer}
            itemSearch={itemSearch}
            iconMap={stoneCutMap}
            trackingOptions={trackingOptions}
        >
            {({ valuesToShow }) => (
                <SbRespRefineMenuStoneCutSelect
                    itemSearch={itemSearch}
                    valuesToShow={valuesToShow}
                    filterName={filterName}
                    stoneCutMap={stoneCutMap}
                    trackingOptions={trackingOptions}
                />
            )}
        </SbSharedRefineMenuTypeaheadContainer>
    );
};
